import { Ban, Download, LoaderCircle, Paperclip, RefreshCw, ShieldAlert } from "lucide-react";
import { useState } from "react";

import { useToast } from "../context/ToastContext";
import { useAttachmentScanStatuses } from '../hooks/useAttachmentScanStatuses';
import { apiRequest } from "../lib/api";
import { formatBytes } from "../lib/format";
import { browserWorkspace, type BrowserWorkspace } from "../lib/workspace";
import type { Attachment } from "../lib/types";
import { AttachmentViewer } from "./AttachmentViewer";

export function MessageAttachments({ attachments, canRetry = false }: { attachments: Attachment[]; canRetry?: boolean }) {
  const { showToast } = useToast();
  const { statusOf, updateStatus, refreshError } = useAttachmentScanStatuses(attachments);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ attachment: Attachment; workspace: BrowserWorkspace } | null>(null);

  async function retry(attachment: Attachment) {
    if (retryingId) return;
    setRetryingId(attachment.id);
    try {
      const response = await apiRequest<{ scanStatus: Attachment["scanStatus"] }>(
        `/attachments/${encodeURIComponent(attachment.id)}/scan/retry`,
        { method: "POST", body: {} },
      );
      updateStatus(attachment, response.scanStatus || 'pending');
      showToast("Attachment security scan queued.", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Attachment scan could not be retried.", "error");
    } finally { setRetryingId(null); }
  }

  return (
    <div className="attachment-list">
      {attachments.map((attachment) => {
        const status = statusOf(attachment);
        if (status === "clean" || attachment.scanBypassed) return (
          <button type="button" className="attachment-list__open" key={attachment.id}
            onClick={() => setPreview({ attachment, workspace: browserWorkspace() })} aria-haspopup="dialog">
            <Paperclip aria-hidden="true" size={15} />
            <span>{attachment.name}<small>{formatBytes(attachment.size)}{attachment.scanBypassed && status !== "clean" ? " · Scanner disabled: download at your own risk" : ""}</small></span>
            <Download aria-hidden="true" size={15} />
          </button>
        );
        const pending = status === "pending";
        const failed = status === "failed";
        return (
          <div className={`attachment-list__item attachment-list__item--${status}`} key={attachment.id}>
            {pending ? <LoaderCircle className="spin" aria-hidden="true" size={15} /> : failed ? <ShieldAlert aria-hidden="true" size={15} /> : <Ban aria-hidden="true" size={15} />}
            <span>
              {attachment.name}
              <small>{formatBytes(attachment.size)} · {pending ? (refreshError ? 'Unable to refresh scan status. Reconnecting...' : 'Security scan in progress') : failed ? 'Security scan failed' : 'Blocked by security scan'}</small>
            </span>
            {failed && canRetry ? (
              <button type="button" disabled={retryingId !== null} onClick={() => void retry(attachment)}>
                <RefreshCw className={retryingId === attachment.id ? "spin" : ""} aria-hidden="true" size={14} />
                {retryingId === attachment.id ? "Queuing…" : "Retry scan"}
              </button>
            ) : <span className="attachment-list__status">Unavailable</span>}
          </div>
        );
      })}
      {preview && <AttachmentViewer key={preview.attachment.id} attachment={preview.attachment}
        workspace={preview.workspace} onClose={() => setPreview(null)} />}
    </div>
  );
}
